import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import { dirOf } from '@/i18n/languages';
import { createAppTheme } from './index';

const STORAGE_KEY = 'sa_theme_mode';

const ThemeModeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

export function ThemeModeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'light';
    } catch {
      return 'light';
    }
  });

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Direction follows the active interface language. Arabic/Urdu need both the
  // MUI theme direction AND an RTL-transformed emotion cache, or padding,
  // margins and icons stay mirrored the wrong way.
  const { i18n } = useTranslation();
  const dir = dirOf(i18n.resolvedLanguage || 'en');

  const theme = useMemo(() => {
    const base = createAppTheme(mode);
    return createTheme({ ...base, direction: dir });
  }, [mode, dir]);

  const cache = useMemo(
    () =>
      createCache(
        dir === 'rtl'
          ? { key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] }
          : { key: 'mui' }
      ),
    [dir]
  );

  const value = useMemo(() => ({ mode, toggleMode, setMode }), [mode, toggleMode, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </ThemeModeContext.Provider>
  );
}

export default ThemeModeProvider;
